import EducationItem from "@/features/events/features/educations/EducationItem";
import EducationSummary from "@/features/events/features/educations/EducationSummary";
import SurgeryItem from "@/features/events/features/surgeries/components/SurgeryItem";
import SurgerySummary from "@/features/events/features/surgeries/components/SurgerySummary";
import InjectionDetailsList from "@/features/injection/components/InjectionDetailsList";
import InjectionMainInfo from "@/features/injection/components/InjectionMainInfo";
import ShockwaveDetailsList from "@/features/shockwave/components/ShockwaveDetailsList";
import ShockwaveMainInfo from "@/features/shockwave/components/ShockwaveMainInfo";
import type {
  EducationT,
  ExtendedEventViewT,
  ExtendedInjectionViewT,
  ExtendedShockwaveViewT,
  SurgeryViewT,
} from "@/types/database/events";
import type { TemplateT } from "@/types/database/quickstarts";
import { Link, useRouter } from "@tanstack/react-router";
import {
  ArrowRight,
  Calendar,
  Clipboard,
  ClipboardEdit,
  Edit,
  Hospital,
  MoreVertical,
  Save,
  Share2,
  Trash,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../components/ui/tabs";

type Template = TemplateT & {
  templates_access: {
    access: "viewer" | "editor";
  }[];
};

function EventsInfo({ settings }) {
  // Helper: Map settings to injection view
  function getInjectionView(): ExtendedInjectionViewT {
    return {
      diary_id: "",
      tracker_id: "",
      event_id: null,
      injection_id: settings.injection_id ?? "",
      injecting_clinician_email: null,
      injecting_clinician_formal_name: null,
      injecting_clinician_id: settings.injecting_clinician_id ?? "",
      injecting_clinician_name: settings.injecting_clinician_id ?? "",
      injecting_clinician_number: null,
      injection_details: (settings.injection_details || []).map((
        d: Record<string, unknown>,
        i: number,
      ) => ({
        injection_detail_id: (d.injection_detail_id as string) || String(i),
        ...d,
      })),
      injection_method: settings.injection_method_id ?? null,
      injection_method_id: settings.injection_method_id ?? null,
      is_incomplete: null,
      referring_clinician_email: null,
      referring_clinician_formal_name: null,
      referring_clinician_id: settings.referring_clinician_id ?? "",
      referring_clinician_name: settings.referring_clinician_id ?? "",
      referring_clinician_number: null,
    };
  }
  // Helper: Map settings to shockwave view
  function getShockwaveView(): ExtendedShockwaveViewT {
    return {
      event_id: "",
      shockwave_id: settings.shockwave_id ?? "",
      is_incomplete: null,
      referring_clinician_email: null,
      referring_clinician_formal_name: null,
      referring_clinician_id: settings.referring_clinician_id ?? "",
      referring_clinician_name: settings.referring_clinician_id ?? "",
      referring_clinician_number: null,
      shockwave_details: (settings.shockwave_details || []).map((
        d: Record<string, unknown>,
        i: number,
      ) => ({
        shockwave_detail_id: (d.shockwave_detail_id as string) || String(i),
        ...d,
      })),
      shockwaving_clinician_email: null,
      shockwaving_clinician_formal_name: null,
      shockwaving_clinician_id: settings.shockwaving_clinician_id ?? "",
      shockwaving_clinician_name: settings.shockwaving_clinician_id ?? "",
      shockwaving_clinician_number: null,
    };
  }

  return (
    <div className="pt-2">
      {settings.required_events?.map((event) => {
        switch (event) {
          case "injection": {
            const injectionView = getInjectionView();
            return (
              <div key="injection" className="space-y-2">
                <InjectionMainInfo injection={injectionView} />
                <InjectionDetailsList
                  injectionDetails={injectionView.injection_details}
                />
              </div>
            );
          }
          case "shockwave": {
            const shockwaveView = getShockwaveView();
            return (
              <div key="shockwave" className="space-y-2">
                <ShockwaveMainInfo shockwave={shockwaveView} />
                <ShockwaveDetailsList
                  shockwaveDetails={shockwaveView.shockwave_details}
                />
              </div>
            );
          }
          case "surgery": {
            const dummySurgery: SurgeryViewT = {
              surgery_id: null,
              surgery_option_id: null,
              orthopaedic_area: null,
              side: null,
              created_at: null,
              event_id: null,
              is_incomplete: null,
              operating_clinician_id: null,
              operating_clinician_name: null,
              referring_clinician_id: null,
              referring_clinician_name: null,
              surgery_name: null,
            };
            const dummyEvent: ExtendedEventViewT = {
              event_id: "",
              diary_id: "",
              patient_id: "",
              contact_id: null,
              created_at: null,
              end_date: null,
              event_name: null,
              event_time: null,
              location: null,
              organisation_id: null,
              shared_attributes: {
                contact_id: null,
                end_date: null,
                hospital_number: null,
                reference_date: null,
                shared_attribute_id: "",
                start_date: null,
              },
              trackers: null,
              injections_view: [],
              surgeries_view: [],
              shockwaves_view: [],
              educations: [],
            };
            return (
              <div key="surgery" className="space-y-2">
                <SurgerySummary surgery={dummySurgery} />
                <SurgeryItem
                  diaryId=""
                  organisationId={settings.organisation_id}
                  eventId=""
                  surgery={dummySurgery}
                  event={dummyEvent}
                  editable={false}
                />
              </div>
            );
          }
          case "education": {
            const dummyEducation: EducationT = {
              created_at: "",
              educated_by: "",
              education_description: null,
              education_id: "",
              education_name: "",
              event_id: "",
            };
            const dummyEvent: ExtendedEventViewT = {
              event_id: "",
              diary_id: "",
              patient_id: "",
              contact_id: null,
              created_at: null,
              end_date: null,
              event_name: null,
              event_time: null,
              location: null,
              organisation_id: null,
              shared_attributes: {
                contact_id: null,
                end_date: null,
                hospital_number: null,
                reference_date: null,
                shared_attribute_id: "",
                start_date: null,
              },
              trackers: null,
              injections_view: [],
              surgeries_view: [],
              shockwaves_view: [],
              educations: [],
            };
            return (
              <div key="education" className="space-y-2">
                <EducationSummary education={dummyEducation} />
                <EducationItem
                  diaryId=""
                  organisationId={settings.organisation_id}
                  eventId=""
                  education={dummyEducation}
                  event={dummyEvent}
                  editable={false}
                />
              </div>
            );
          }
          default:
            return null;
        }
      })}
    </div>
  );
}

export default EventsInfo;
